<template>
  <popup v-model="isOpened" :loading="loading || loader.on" min-width="850px" title="VPC Peering" hide-footer>
    <div id="multiVpc">
      <p>{{ t("explanation") }}</p>

      <div v-if="registeredVpcId" class="vpcInfo">
        <p>
          <span class="label">VPC ID:</span>
          <span>{{ registeredVpcId }}</span>
        </p>

        <div class="buttons">
          <v-btn @click="copyVpcId" text icon>
            <v-icon>action_copy</v-icon>
          </v-btn>

          <tooltip :text="t('openAwsConsole')">
            <v-btn @click="openAwsConsole" text icon>
              <img :src="VizNowAwsIcon" alt="aws logo" />
            </v-btn>
          </tooltip>
        </div>
      </div>

      <div class="spacesContainer">
        <div class="spaceList">
          <div class="scrollContainer">
            <ul>
              <li
                v-for="(vpcItem, index) in vpcCollection"
                @click="selectVpc(vpcItem)"
                :key="vpcItem.id"
                :showing="isShowing(vpcItem)"
                :class="`animationDelay-${index}`"
                class="spaceItem animateFadeUpFast"
              >
                <span class="vpcStatus">
                  <v-icon v-if="isNew(vpcItem)" color="warning">status_warning</v-icon>

                  <tooltip
                    v-else-if="showIcon(vpcItem.peeringStatus)"
                    :text="$t(`WorkspaceEdit.vpcPeering.status.${vpcItem.peeringStatus}`)"
                  >
                    <v-icon :size="16" class="spinning" color="#fff">status_spinner_indeterminate</v-icon>
                  </tooltip>

                  <span v-else :status="vpcItem.peeringStatus" class="statusCircle" />
                </span>

                <span class="vpcName">{{ vpcItem.name || "Untitled" }}</span>

                <span v-if="isExternalVpc(vpcItem) && !isShowing(vpcItem)" class="externalLabel">
                  <small>{{ t("external") }}</small>
                </span>

                <span v-else-if="isShowing(vpcItem)" class="statusLabel">
                  <small>{{ translateVpcStatus(vpcItem) }}</small>
                </span>
              </li>

              <li v-if="vpcCollection.length === 0" class="emptyList">
                <small>{{ t("noPeering") }}</small>
              </li>
            </ul>
          </div>

          <div class="createNewPeering">
            <v-menu>
              <template v-slot:activator="{ on, attrs }">
                <spotlight-element :label="t('spotlightCreateNewVpc')" :active="createVpcIndicatorIsActive" right>
                  <v-btn v-bind="attrs" v-on="on" rbt-target="moreOptionsBtn" icon>
                    <v-icon>action_add_circle</v-icon>
                  </v-btn>
                </spotlight-element>
              </template>

              <ul class="listMenu">
                <li @click="startNewPeering(CreationType.SPACE_PEERING)" class="listRow" rbt-target="editUserBtn">
                  <span>{{ t("addNewVpcOptions.peerWithSpace") }}</span>
                </li>

                <li @click="startNewPeering(CreationType.EXTERNAL_PEERING)" class="listRow" rbt-target="editUserBtn">
                  <span>{{ t("addNewVpcOptions.externalPeering") }}</span>
                </li>
              </ul>
            </v-menu>
          </div>
        </div>

        <div class="newVpcSection">
          <vpc-watcher
            v-if="vpc"
            v-model="vpc"
            :vpc-collection="vpcCollection"
            @remove:new-vpc="removeNewVpc"
            @remove:old-vpc="removeOldVpc"
            @create:new-vpc="createVpc"
          />
        </div>
      </div>
    </div>
  </popup>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from "@vue/composition-api"
import { createTranslationModule, translate } from "@/plugins/i18n"
import { PeeringStatus, CreationType, AwsVpcConfig, VpcPeerWorkspace } from "./lib/type"
import { createVpcStructure } from "./lib/vpc-structure"
import { VpcItem } from "./lib/type"
import { raiseConfirmation, raiseError, raiseSuccess } from "@/utils/event-bus"
import { getAwsConsoleUrl } from "@/config/aws-console"
import { copyToClipboard } from "@/utils/ui"
import { useStore } from "@/composable/useStore"
import { defaults } from "./lib/defaults"
import Popup from "@/components/popup/PopupRoot.vue"
import Tooltip from "@/components/tooltip/TooltipRoot.vue"
import Utils from "@/utils/utils"
import VpcWatcher from "./_VpcWatcher.vue"
import SpotlightElement from "@/components/spotlightElement"
import Loader from "@/utils/loader"
import VizNowAwsIcon from "@/assets/viznow-aws-icon.svg"
import "./vpc-peering.scss"

export default defineComponent({
  name: "MultiVpc",
  props: {
    value: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  setup(props, ctx) {
    const { spaceModule } = useStore()
    const t = createTranslationModule("WorkspaceEdit.multiVpc")
    const _isCreatingNew = ref(false)
    const vpcList = ref<VpcItem[]>([])
    const vpcId = ref<null | string>(null)
    const isOpened = Utils.vModel(props, ctx.emit)
    const creationType = ref<CreationType | null>(null)
    const loader = Loader()
    const vpc = ref<VpcItem | null>(null)

    const vpcCollection = computed(() => {
      const newItems = vpcList.value
      const createdVpcs = spaceModule.vpc

      return [...newItems, ...createdVpcs] as VpcItem[]
    })

    const createVpcIndicatorIsActive = computed(() => {
      return vpcCollection.value.length === 0
    })

    const registeredVpcId = computed(() => {
      return spaceModule.watching.vpcId || null
    })

    const isNew = (vpc: VpcItem) => {
      return vpc?.isNew || false
    }

    const showIcon = (vpcStatus: PeeringStatus | null) => {
      const { initiatingRequest, pendingAcceptance, provisioning, deleting } = PeeringStatus

      if (vpcStatus) {
        return [initiatingRequest, pendingAcceptance, provisioning, deleting].includes(vpcStatus)
      }
      return false
    }

    const startNewPeering = (peeringType: CreationType) => {
      const newVpc = createVpcStructure()

      newVpc.creationType = peeringType

      if (peeringType === CreationType.EXTERNAL_PEERING) {
        newVpc.name = defaults.CUSTOM_PEERING_NAME
      } else {
        newVpc.name = defaults.SPACE_PEERING
      }

      vpcList.value.unshift(newVpc)
      _selectFirstVpcFromCollection()
    }

    const isPeeringType = (peeringType: CreationType) => {
      return peeringType === creationType.value && _isCreatingNew.value
    }

    const selectVpc = (vpcItem: VpcItem) => {
      vpcId.value = vpcItem.id
      vpc.value = vpcCollection.value.find(_vpc => _vpc.id === vpcId.value) as VpcItem
    }

    const isShowing = (vpc: VpcItem) => {
      return vpc.id === vpcId.value
    }

    const openAwsConsole = () => {
      const spaceRegion = spaceModule.watching?.cloudZone

      if (registeredVpcId.value && spaceRegion) {
        const consoleUrl = getAwsConsoleUrl(spaceRegion, registeredVpcId.value)
        window.open(consoleUrl, "_blank")
      } else {
        raiseError({ text: t("prompt.onOpenAwsConsoleError") })
      }
    }

    const translateVpcStatus = (vpc: VpcItem) => {
      const isNew = vpc.isNew

      if (!isNew) {
        if (vpc.peerWorkspaceId) {
          return translate("WorkspaceEdit.vpcPeering.status__spacePeering." + vpc.peeringStatus)
        } else {
          return translate("WorkspaceEdit.vpcPeering.status__customPeering." + vpc.peeringStatus)
        }
      }

      return translate("WorkspaceEdit.vpcPeering.status__customPeering.null")
    }

    const removeNewVpc = (vpcId: string) => {
      const vpcIndexInCollection = vpcList.value.findIndex(vpc => vpc.id === vpcId)

      if (vpcIndexInCollection !== -1) {
        vpcList.value.splice(vpcIndexInCollection, 1)
        _selectFirstVpcFromCollection()
      }
    }

    const createVpc = (vpc: AwsVpcConfig | VpcPeerWorkspace) => {
      loader.run(async () => {
        const spaceId = spaceModule.watching.id

        await spaceModule.saveVpc(vpc, spaceId)
        removeNewVpc(vpc.id)
        await _updateVpcCollection()
      })
    }

    const isExternalVpc = (vpc: VpcItem) => {
      return Boolean(!vpc.peerWorkspaceId) && !vpc.isNew
    }

    const copyVpcId = async () => {
      if (registeredVpcId.value) {
        try {
          await copyToClipboard(registeredVpcId.value)
          raiseSuccess({ text: t("prompt.onCopy") })
        } catch {
          raiseError({ text: t("prompt.onError") })
        }
      }
    }

    const removeOldVpc = async (peeringId: string) => {
      if (await raiseConfirmation({ text: translate("WorkspaceEdit.multiVpc.confirmRemoveVpc") })) {
        loader.run(async () => {
          const spaceId = spaceModule.watching.id

          await spaceModule.deleteVpc(spaceId, peeringId)
          await _updateVpcCollection()
        })
      }
    }

    const _updateVpcCollection = async () => {
      const spaceId = spaceModule.watching.id

      await spaceModule.getVpc(spaceId)
      _selectFirstVpcFromCollection()
    }

    const _resetWatchingVpc = () => {
      vpcId.value = null
      vpc.value = null
    }

    const _selectFirstVpcFromCollection = () => {
      if (vpcCollection.value.length > 0) {
        const firstVpc = vpcCollection.value[0]
        selectVpc(firstVpc)
      } else {
        _resetWatchingVpc()
      }
    }

    loader.run(async () => {
      const organizationId = spaceModule.watching.organizationId

      await _updateVpcCollection()
      await spaceModule.getMyWorkspacesUsingOrganizationId(organizationId)
    })

    return {
      registeredVpcId,
      vpcId,
      copyVpcId,
      vpcList,
      VizNowAwsIcon,
      t,
      showIcon,
      loader,
      isExternalVpc,
      openAwsConsole,
      translateVpcStatus,
      isNew,
      removeOldVpc,
      removeNewVpc,
      createVpc,
      vpc,
      isShowing,
      isOpened,
      selectVpc,
      createVpcIndicatorIsActive,
      vpcCollection,
      startNewPeering,
      isPeeringType,
      CreationType
    }
  },
  components: {
    SpotlightElement,
    Tooltip,
    Popup,
    VpcWatcher
  }
})
</script>
